import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBenefitProgramsV1ProgramDefinition from './benefit-programs-v1-program-definition-program-definitions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface ProgramDefinition {
  /**
   * Program definition ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the program definition is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the program definition.
   *
   * Ignored when creating a program definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the program definition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the program definition was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Program definition name.
   * @maxLength 64
   */
  displayName?: string | null;
  /**
   * Program definition description.
   * @internal
   * @maxLength 450
   */
  description?: string | null;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @immutable
   * @readonly
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string | null;
  /**
   * Custom field data for the program definition object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * ID for the program definition defined by you. You can use `externalId` to filter queries.
   * @format GUID
   * @immutable
   * @readonly
   */
  externalId?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ProgramDefinitionTranslated {
  /** The translated program definition. */
  programDefinition?: ProgramDefinition;
  /**
   * The language code of the translation.
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  language?: string;
}

export interface ProgramDefinitionTranslationDeleted {
  /**
   * The ID of the program definition.
   * @format GUID
   */
  programDefinitionId?: string;
  /**
   * The language code of the translation.
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  language?: string;
}

export interface CreateProgramDefinitionRequest {
  /** Program definition to create. */
  programDefinition: ProgramDefinition;
}

export interface CreateProgramDefinitionResponse {
  /** Created program definition. */
  programDefinition?: ProgramDefinition;
}

export interface UpdateProgramDefinitionRequest {
  /** Program definition to update. */
  programDefinition: ProgramDefinition;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateProgramDefinitionResponse {
  /** Updated program definition. */
  programDefinition?: ProgramDefinition;
}

export interface DeleteProgramDefinitionRequest {
  /**
   * ID of the program definition to delete.
   * @format GUID
   */
  programDefinitionId: string;
  /** Determines when the changes to this program definition will be applied to associated programs and pools. */
  cascadeType?: CascadeWithLiterals;
}

export enum Cascade {
  /** Unknown cascade. */
  UNKNOWN_CASCADE = 'UNKNOWN_CASCADE',
  /** Changes are applied to existing programs and pools when they are next renewed. */
  NEXT_RENEWAL = 'NEXT_RENEWAL',
  /** Changes are applied to associated programs and pools immediately. */
  IMMEDIATELY = 'IMMEDIATELY',
  /** Changes are not applied to existing associated programs and pools. They are only applied to future programs and pools. */
  FUTURE_PROVISIONS = 'FUTURE_PROVISIONS',
}

/** @enumType */
export type CascadeWithLiterals =
  | Cascade
  | 'UNKNOWN_CASCADE'
  | 'NEXT_RENEWAL'
  | 'IMMEDIATELY'
  | 'FUTURE_PROVISIONS';

export interface DeleteProgramDefinitionResponse {}

export interface GetProgramDefinitionRequest {
  /**
   * ID of the program definition to retrieve.
   * @format GUID
   */
  programDefinitionId: string;
}

export interface GetProgramDefinitionResponse {
  /** Retrieved program definition. */
  programDefinition?: ProgramDefinition;
}

export interface GetProgramDefinitionByExternalIdAndNamespaceRequest {
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @minLength 1
   * @maxLength 20
   */
  namespace: string;
  /**
   * Program definition external ID.
   * @format GUID
   */
  externalId: string;
}

export interface GetProgramDefinitionByExternalIdAndNamespaceResponse {
  /** Retrieved program definition. */
  programDefinition?: ProgramDefinition;
}

export interface QueryProgramDefinitionsRequest {
  /** Filter, sort, and paging to apply to the query. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
}

export enum SortOrder {
  /** Ascending sort order. */
  ASC = 'ASC',
  /** Descending sort order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryProgramDefinitionsResponse {
  /** List of retrieved program definitions. */
  programDefinitions?: ProgramDefinition[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpsertProgramDefinitionRequest {
  /** Program definition to be created or updated. */
  programDefinition?: ProgramDefinition;
  /**
   * Explicit list of fields to update
   * @internal
   */
  fieldMask?: string[];
}

export interface UpsertProgramDefinitionResponse {
  /** Created or updated program definition. */
  programDefinition?: ProgramDefinition;
}

export interface GetOrCreateProgramDefinitionRequest {
  /**
   * Application sub-module that represents the source of the program definition. Specified by the external developer. This namespace is used to handle and retrieve program definition and associated programs.
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string;
  /**
   * External program definition ID.
   * @format GUID
   */
  externalId?: string;
}

export interface GetOrCreateProgramDefinitionResponse {
  /** The created or updated program definition. */
  programDefinition?: ProgramDefinition;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Creates a program definition.
 * @param programDefinition - Program definition to create.
 * @public
 * @requiredField programDefinition
 * @requiredField programDefinition.externalId
 * @requiredField programDefinition.namespace
 * @permissionId BENEFIT_PROGRAMS.PROGRAM_DEFINITION_CREATE
 * @applicableIdentity APP
 * @returns Created program definition.
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.CreateProgramDefinition
 */
export async function createProgramDefinition(
  programDefinition: NonNullablePaths<
    ProgramDefinition,
    `externalId` | `namespace`,
    2
  >
): Promise<ProgramDefinition> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programDefinition: programDefinition,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1ProgramDefinition.createProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.programDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { programDefinition: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['programDefinition']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a program definition.
 *
 * Each time the program definition is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the program definition.
 * This ensures you're working with the latest program definition
 * and prevents unintended overwrites.
 * @param _id - Program definition ID.
 * @public
 * @requiredField _id
 * @requiredField programDefinition
 * @requiredField programDefinition.revision
 * @permissionId BENEFIT_PROGRAMS.PROGRAM_DEFINITION_UPDATE
 * @applicableIdentity APP
 * @returns Updated program definition.
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.UpdateProgramDefinition
 */
export async function updateProgramDefinition(
  _id: string,
  programDefinition: NonNullablePaths<UpdateProgramDefinition, `revision`, 2>
): Promise<ProgramDefinition> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programDefinition: { ...programDefinition, id: _id },
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1ProgramDefinition.updateProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.programDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { programDefinition: '$[1]' },
        explicitPathsToArguments: { 'programDefinition.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'programDefinition']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateProgramDefinition {
  /**
   * Program definition ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the program definition is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the program definition.
   *
   * Ignored when creating a program definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the program definition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the program definition was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Program definition name.
   * @maxLength 64
   */
  displayName?: string | null;
  /**
   * Program definition description.
   * @internal
   * @maxLength 450
   */
  description?: string | null;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @immutable
   * @readonly
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string | null;
  /**
   * Custom field data for the program definition object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * ID for the program definition defined by you. You can use `externalId` to filter queries.
   * @format GUID
   * @immutable
   * @readonly
   */
  externalId?: string | null;
}

/**
 * Deletes a program definition.
 * @param programDefinitionId - ID of the program definition to delete.
 * @public
 * @requiredField programDefinitionId
 * @permissionId BENEFIT_PROGRAMS.PROGRAM_DEFINITION_DELETE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.DeleteProgramDefinition
 */
export async function deleteProgramDefinition(
  programDefinitionId: string,
  options?: DeleteProgramDefinitionOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programDefinitionId: programDefinitionId,
    cascadeType: options?.cascadeType,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1ProgramDefinition.deleteProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          programDefinitionId: '$[0]',
          cascadeType: '$[1].cascadeType',
        },
        singleArgumentUnchanged: false,
      },
      ['programDefinitionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteProgramDefinitionOptions {
  /** Determines when the changes to this program definition will be applied to associated programs and pools. */
  cascadeType?: CascadeWithLiterals;
}

/**
 * Retrieves a program definition.
 * @param programDefinitionId - ID of the program definition to retrieve.
 * @public
 * @requiredField programDefinitionId
 * @permissionId BENEFIT_PROGRAMS.PROGRAM_DEFINITION_READ
 * @applicableIdentity APP
 * @returns Retrieved program definition.
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.GetProgramDefinition
 */
export async function getProgramDefinition(
  programDefinitionId: string
): Promise<ProgramDefinition> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programDefinitionId: programDefinitionId,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1ProgramDefinition.getProgramDefinition(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.programDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { programDefinitionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['programDefinitionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a program definition with the specified external ID and namespace.
 * @param namespace - Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
 * @public
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.externalId
 * @permissionId BENEFIT_PROGRAMS.PROGRAM_DEFINITION_READ
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.GetProgramDefinitionByExternalIdAndNamespace
 */
export async function getProgramDefinitionByExternalIdAndNamespace(
  namespace: string,
  options: NonNullablePaths<
    GetProgramDefinitionByExternalIdAndNamespaceOptions,
    `externalId`,
    2
  >
): Promise<GetProgramDefinitionByExternalIdAndNamespaceResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    namespace: namespace,
    externalId: options?.externalId,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1ProgramDefinition.getProgramDefinitionByExternalIdAndNamespace(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          namespace: '$[0]',
          externalId: '$[1].externalId',
        },
        singleArgumentUnchanged: false,
      },
      ['namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetProgramDefinitionByExternalIdAndNamespaceOptions {
  /**
   * Program definition external ID.
   * @format GUID
   */
  externalId: string;
}

/**
 * Creates a query to retrieve a list of program definitions.
 *
 * The Query Program Definitions method builds a query to retrieve a list of program definitions and returns a `ProgramDefinitionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `ProgramDefinitionsQueryBuilder` methods onto the query. `ProgramDefinitionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Program Definitions returns.
 *
 * Query Program Definitions has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `ProgramDefinitionsQueryResult`.
 * @public
 * @permissionId BENEFIT_PROGRAMS.PROGRAM_DEFINITION_READ
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.QueryProgramDefinitions
 */
export function queryProgramDefinitions(): ProgramDefinitionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    ProgramDefinition,
    'CURSOR',
    QueryProgramDefinitionsRequest,
    QueryProgramDefinitionsResponse
  >({
    func: async (payload: QueryProgramDefinitionsRequest) => {
      const reqOpts =
        ambassadorWixBenefitProgramsV1ProgramDefinition.queryProgramDefinitions(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryProgramDefinitionsRequest['query']) => {
      const args = [query, {}] as [QueryProgramDefinitionsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryProgramDefinitionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.programDefinitions,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ProgramDefinitionsQueryResult extends QueryCursorResult {
  items: ProgramDefinition[];
  query: ProgramDefinitionsQueryBuilder;
  next: () => Promise<ProgramDefinitionsQueryResult>;
  prev: () => Promise<ProgramDefinitionsQueryResult>;
}

export interface ProgramDefinitionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'namespace' | 'externalId',
    value: string
  ) => ProgramDefinitionsQueryBuilder;
  in: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: any
  ) => ProgramDefinitionsQueryBuilder;
  exists: (
    propertyName: '_id' | '_createdDate' | 'namespace' | 'externalId',
    value: boolean
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate' | 'namespace' | 'externalId'>
  ) => ProgramDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate' | 'namespace' | 'externalId'>
  ) => ProgramDefinitionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ProgramDefinitionsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ProgramDefinitionsQueryBuilder;
  find: () => Promise<ProgramDefinitionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.benefit_programs.v1.program_definition.ProgramDefinitionService.QueryProgramDefinitions
 * @requiredField query
 */
export async function typedQueryProgramDefinitions(
  query: ProgramDefinitionQuery
): Promise<
  NonNullablePaths<QueryProgramDefinitionsResponse, `programDefinitions`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixBenefitProgramsV1ProgramDefinition.queryProgramDefinitions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ProgramDefinitionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', 'externalId', 'namespace'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  ProgramDefinition,
  ProgramDefinitionQuerySpec
>;
export type ProgramDefinitionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.
  See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
  for more information.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  List of sort objects. 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order. Use `ASC` for ascending order or `DESC` for descending order.

  Default: `ASC`  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
    /** 
  When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
  a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.

  If multiple filters are provided, they are combined with AND operator.

  Example:
  Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
  and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
  { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] } 
  @internal: undefined,
  @maxSize: 10 
  */
    selectItemsBy?:
      | NonNullable<
          CommonQueryWithEntityContext['sort']
        >[number]['selectItemsBy']
      | null;
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    ProgramDefinition,
    ProgramDefinitionQuerySpec,
    ProgramDefinitionQuery
  >(),
};
